/**
 * Small display helpers — date/time, status pill text, etc. Kept here so the
 * screens stay focused on layout.
 */

import { jobStatusLabel } from '@cer-autofill/shared';
import type {
  AdminReportStatus,
  FieldReadiness,
  JobStatus,
  InspectionType,
  OutstandingDisposition,
  QaFindingOrigin,
  QaStatus,
} from '@cer-autofill/shared';

/**
 * Format a naive-or-Z-tagged ISO string as a short local-ish display string.
 * The wire spec allows both forms; we render them the same way.
 */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Clock time for an availability event, as HH:MM.
 *
 * Two wire formats meet here and they are not interchangeable. ServiceM8
 * timestamps are naive local wall-clock (`2026-08-05 08:00:00`) — the hours are
 * already what the inspector's day looked like, so they are read literally.
 * Blocks declared in the dashboard are true UTC instants (`…T22:00:00.000Z`),
 * which must be converted or an 8am block reads as 22:00.
 */
export function formatEventTime(value: string): string {
  const instant = /[Tt].*(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(value);
  if (!instant) return value.slice(11, 16);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(11, 16);
  return parsed.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatDateOnly(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/**
 * The report's QA outcome. The LIFECYCLE (Booked … Submitted to CER,
 * Complete) belongs to the job and is labelled by `jobStatusLabel` — see
 * docs/GLOSSARY.md. Keeping the two vocabularies apart is the point: one
 * journey, one record that describes it.
 */
export function statusLabel(s: AdminReportStatus): string {
  switch (s) {
    case 'pending':
      return 'Awaiting QA';
    case 'approved':
      return 'QA passed';
    case 'information-required':
      return 'Additional information required';
    case 'revisit-required':
      return 'Return to site';
  }
}

export function statusPillClass(s: AdminReportStatus): string {
  return `pill pill--${s}`;
}

/* ── Case lifecycle (M2 intake — shared/src/cases.ts) ─────────────────────── */

export function jobStatusPillClass(s: JobStatus): string {
  return `pill pill--job-${s}`;
}

export { jobStatusLabel };

/** Short badge text for the inspection type column. */
export function inspectionTypeLabel(t: InspectionType): string {
  return t === 'battery' ? 'BTY' : 'PV';
}

export function inspectionTypeBadgeClass(t: InspectionType): string {
  return `type-badge type-badge--${t}`;
}

/* ── QA rule chips (shared/src/qa.ts) ─────────────────────────────────────── */

export function qaStatusLabel(s: QaStatus): string {
  switch (s) {
    case 'pass':
      return 'Pass';
    case 'fail':
      return 'Fail';
    case 'warn':
      return 'Warn';
  }
}

/** Reuses the clause-count chip styling the dashboard already ships. */
export function qaChipClass(s: QaStatus): string {
  switch (s) {
    case 'pass':
      return 'clause-count clause-count--c';
    case 'fail':
      return 'clause-count clause-count--nc';
    case 'warn':
      return 'clause-count clause-count--warn';
  }
}

/* ── Field readiness (shared/src/field-readiness.ts) ──────────────────────── */

/**
 * How many of the field tool's checks are still open.
 *
 * Deliberately distinct from the report STATUS pill next to it: status is
 * where the report writer has taken it, readiness is what is still outstanding.
 */
export function readinessLabel(r: FieldReadiness): string {
  return r.state === 'needs-work' ? `Needs work · ${r.outstandingCount}` : 'Ready for QA';
}

export function readinessPillClass(r: FieldReadiness): string {
  if (r.state === 'needs-work') return 'pill pill--needs-work';
  // A legacy submission gets the quieter pill: it is complete by definition,
  // but nothing on the device actually asserted that.
  return r.legacy ? 'pill pill--ready-legacy' : 'pill pill--ready';
}

export function readinessTitle(r: FieldReadiness): string {
  if (r.state === 'needs-work') {
    return `${r.outstandingCount} item${r.outstandingCount === 1 ? '' : 's'} still outstanding.`;
  }
  return r.legacy
    ? 'Outstanding field checks were not recorded for this submission.'
    : 'Nothing outstanding.';
}

export function qaOriginLabel(o: QaFindingOrigin): string {
  switch (o) {
    case 'inspector-known':
      return 'Inspector flagged';
    case 'qa-found':
      return 'QA found';
    case 'unrecorded':
      return 'No submit record';
  }
}

export function qaOriginChipClass(o: QaFindingOrigin): string {
  return `origin-chip origin-chip--${o}`;
}

export function qaOriginTitle(o: QaFindingOrigin): string {
  switch (o) {
    case 'inspector-known':
      return 'The inspector recorded this same gap at submit — expected work, not a surprise.';
    case 'qa-found':
      return 'Not flagged at submit. The inspector believed this was clean.';
    case 'unrecorded':
      return 'This submission predates outstanding-at-submit recording, so there is no record either way.';
  }
}

/**
 * Phrased for mid-sentence use ("2 still open in QA · 1 resolved since
 * submit") — lowercase except the acronym, which must not be down-cased.
 */
export function dispositionLabel(d: OutstandingDisposition): string {
  switch (d) {
    case 'open':
      return 'still open in QA';
    case 'resolved':
      return 'resolved since submit';
    case 'not-qa-checked':
      return 'not covered by QA';
    case 'unclassified':
      return 'not recognised';
  }
}
